import { SurfaceState } from 'lib/a2ui'
import { A2UIComponent, DynamicBool, DynamicNumber, DynamicString } from 'pbts/c1/api/a2ui/v1/components'
import { UIMessage } from 'pbts/c1/api/conversations/v1/conversations'
import { Timestamp } from 'pbts/google/protobuf/timestamp'
import { ReactNode } from 'react'

/** No-op function for placeholder callbacks */
export const noop = (): void => { /* intentionally empty */ }

/**
 * Common props passed to all A2UI component renderers.
 */
export type RendererProps = {
    component: A2UIComponent
    componentsMap: Map<string, A2UIComponent>
    dataModel: Record<string, unknown>
    onDataChange: (path: string, value: unknown) => void
    onAction: (actionName: string, context: Record<string, string>, sourceComponentId: string) => void
    disabled?: boolean
    loadingComponents?: Set<string>
    renderChildren: (childIds: string[]) => ReactNode
}

/**
 * Timeline item representing either a chat message or an A2UI surface,
 * used for chronological interleaving in the conversation view.
 */
export type TimelineItem =
    | { type: 'message'; id: string; ts: Timestamp; data: UIMessage }
    | { type: 'surface'; id: string; ts: Timestamp; data: SurfaceState }

/**
 * Resolves a DynamicString to its actual string value by checking if it's
 * a literal value or a path reference into the data model.
 */
export const resolveDynamicString = (
    ds: DynamicString | undefined,
    dataModel: Record<string, unknown>,
    getByPointer: (obj: Record<string, unknown>, pointer: string) => unknown,
): string => {
    if (!ds) return ''
    switch (ds.value.oneofKind) {
        case 'literal':
            return ds.value.literal
        case 'path':
            return String(getByPointer(dataModel, ds.value.path) ?? '')
        case 'call':
            return ''
        default:
            return ''
    }
}

/**
 * Resolves a DynamicBool to its actual boolean value by checking if it's
 * a literal value or a path reference into the data model.
 */
export const resolveDynamicBool = (
    db: DynamicBool | undefined,
    dataModel: Record<string, unknown>,
    getByPointer: (obj: Record<string, unknown>, pointer: string) => unknown,
): boolean => {
    if (!db) return false
    switch (db.value.oneofKind) {
        case 'literal':
            return db.value.literal
        case 'path':
            return Boolean(getByPointer(dataModel, db.value.path))
        case 'call':
            return false
        default:
            return false
    }
}

/**
 * Resolves a DynamicNumber to its actual number value by checking if it's
 * a literal value or a path reference into the data model.
 */
export const resolveDynamicNumber = (
    dn: DynamicNumber | undefined,
    dataModel: Record<string, unknown>,
    getByPointer: (obj: Record<string, unknown>, pointer: string) => unknown,
    defaultValue = 0,
): number => {
    if (!dn) return defaultValue
    switch (dn.value.oneofKind) {
        case 'literal':
            return dn.value.literal
        case 'path': {
            const resolved = Number(getByPointer(dataModel, dn.value.path))
            return Number.isFinite(resolved) ? resolved : defaultValue
        }
        case 'call':
            return defaultValue
        default:
            return defaultValue
    }
}

/**
 * Extracts the path from a DynamicString if it uses path-based resolution.
 * Returns undefined for literal or call-based dynamic strings.
 */
export const getStringPath = (ds: DynamicString | undefined): string | undefined => {
    if (ds?.value.oneofKind === 'path') {
        return ds.value.path
    }
    return undefined
}

/**
 * Extracts the path from a DynamicBool if it uses path-based resolution.
 * Returns undefined for literal or call-based dynamic bools.
 */
export const getBoolPath = (db: DynamicBool | undefined): string | undefined => {
    if (db?.value.oneofKind === 'path') {
        return db.value.path
    }
    return undefined
}
